import { ErrorFn } from 'apps/sigma2-ui/src/lib/read-write-patterns';
import { WriteCompleteFn } from 'apps/sigma2-ui/src/lib/read-write-patterns';
import { WriteFn } from 'apps/sigma2-ui/src/lib/read-write-patterns';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { IsAliveFn } from 'apps/sigma2-ui/src/lib/hooks/useAlive';
import { S2SafeParsable } from 'libs/end-to-end/src/interfaces';
import { useMemo } from 'react';

interface WriteFnHook<TRead, TWrite> {
  writeFn: WriteFn<TRead, TWrite>;
  isReadyToWrite: boolean;
}

export const useWriteFn = <TRead, TWrite>({
  getUrlFn,
  canWrite,
  responseSchema,
  method,
}: {
  getUrlFn: () => string | null;
  canWrite: boolean;
  writeSchema: S2SafeParsable<unknown, TWrite>;
  responseSchema: S2SafeParsable<unknown, TRead>;
  method?: 'PUT' | 'POST' | 'DELETE';
}): WriteFnHook<TRead, TWrite> => {
  const { myUserStatus, userAuthenticatedApiCall } = useUser();

  const isReadyToWrite = myUserStatus === 'logged_in' && canWrite;

  const writeFn = useAsyncCallback(
    async (
      isAlive: IsAliveFn,
      request: TWrite,
      complete: WriteCompleteFn<TRead>,
      error: ErrorFn,
    ) => {
      if (!isAlive() || !isReadyToWrite) {
        return;
      }

      const url = getUrlFn();

      if (!url) {
        return;
      }

      const result = await userAuthenticatedApiCall({
        responseSchema,
        url,
        requestOptions: {
          method: method ?? 'PUT',
          body: JSON.stringify(request),
        },
      });

      if (!isAlive() || !result) {
        return;
      }

      if (result.resultType === 'err') {
        error(result.error);
        return;
      }

      complete(result.value);
    },
    [
      isReadyToWrite,
      method,
      responseSchema,
      getUrlFn,
      userAuthenticatedApiCall,
    ],
  );

  return useMemo(() => {
    return {
      writeFn,
      isReadyToWrite,
    };
  }, [isReadyToWrite, writeFn]);
};
